import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, RefreshCw, Trash2, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" }) : "—");
const btn = "flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold";

const ProviderBadge = ({ p }) => (
  <span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${p === "google" ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-gold/30 bg-gold/10 text-gold"}`}>
    {p === "google" ? "Google" : "Email & password"}
  </span>
);

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("all");
  const [cred, setCred] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reveal = async (u) => {
    try { const { data } = await api.get(`/admin/users/${u.id}/credentials`); setCred({ ...data, user: u }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const regenerate = async (u) => {
    if (!window.confirm(`Generate a new password for ${u.email}? It will be emailed to them.`)) return;
    try { const { data } = await api.post(`/admin/users/${u.id}/reset-password`); setCred({ ...data, user: u, auth_provider: u.auth_provider }); toast.success("New password generated and emailed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}'s profile? Personal data and documents are removed permanently; applications stay as "Deleted candidate". They must register again to apply.`)) return;
    try { await api.delete(`/admin/candidates/${u.id}`); toast.success("Profile deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const shown = users.filter((u) => role === "all" || u.role === role);

  return (
    <div>
      <p className="eyebrow">Access</p>
      <h1 className="mt-3 font-serif text-3xl tracking-tight">Users & Credentials</h1>
      <p className="mt-2 font-sans text-sm text-[#94A3B8]">Every account created on the portal. Viewing a password is recorded in the audit log.</p>
      <div className="mt-6 flex gap-2">
        {["all", "candidate", "admin"].map((r) => (
          <button key={r} onClick={() => setRole(r)} data-testid={`users-filter-${r}`} className={`rounded-full border px-4 py-1.5 font-sans text-xs uppercase tracking-widest ${role === r ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-[#94A3B8]"}`}>{r} ({r === "all" ? users.length : users.filter((u) => u.role === r).length})</button>
        ))}
      </div>
      <div className="mt-6 overflow-x-auto rounded-lg border border-white/5">
        <table className="w-full min-w-[1000px] text-left">
          <thead className="bg-[#18202E]">
            <tr className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
              {["Name", "Email / Phone", "Role", "Signed up with", "Created", "Last login", "Apps", "Actions"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-white/5 align-top">
                <td className="px-4 py-3 font-sans text-sm">{u.name}</td>
                <td className="px-4 py-3 font-sans text-sm text-[#94A3B8]">{u.email}<br /><span className="text-xs">{u.phone || "—"}</span></td>
                <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gold">{u.role}</td>
                <td className="px-4 py-3"><ProviderBadge p={u.auth_provider} /></td>
                <td className="px-4 py-3 font-sans text-xs text-[#94A3B8]">{fmt(u.created_at)}</td>
                <td className="px-4 py-3 font-sans text-xs text-[#94A3B8]">{fmt(u.last_login_at)}</td>
                <td className="px-4 py-3 font-serif text-lg text-gold">{u.application_count}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => reveal(u)} data-testid={`user-cred-${u.id}`} className={btn}><KeyRound size={13} /> Credentials</button>
                    <button onClick={() => regenerate(u)} data-testid={`user-regen-${u.id}`} className={btn}><RefreshCw size={13} /> New password</button>
                    {u.role === "candidate" && <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="flex items-center gap-1.5 rounded-md border border-rose-500/40 px-2.5 py-1.5 font-sans text-xs text-rose-300 hover:bg-rose-500/10"><Trash2 size={13} /> Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center font-sans text-sm text-[#94A3B8]">No accounts.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cred} onOpenChange={(o) => !o && setCred(null)}>
        <DialogContent className="max-w-md border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Login credentials</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">{cred?.user?.name} · <ProviderBadge p={cred?.auth_provider} /></DialogDescription>
          </DialogHeader>
          {cred && (
            <div className="space-y-3 font-sans text-sm">
              {[["Login ID", cred.login_id], ["Password", cred.password]].map(([l, v]) => (
                <div key={l} className="flex items-center justify-between rounded-md border border-white/5 bg-[#0A0D14] px-4 py-3">
                  <div><p className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">{l}</p><p className="font-mono" data-testid={`cred-${l === "Login ID" ? "login" : "password"}`}>{v || "Not stored — use New password"}</p></div>
                  {v && <button onClick={() => copy(v)} className={btn}><Copy size={13} /></button>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
